import { useSelector } from 'react-redux';
import { Icon, Surface, Txt } from '@vst/beam';
import { RemoveCircleOutline } from '@vst/beam-icons/icons';

import { selectCurrencySymbol, selectLocale } from '@buy-viasat/redux/src/app';
import { priceDecimalFormat } from '@buy-viasat/utils';
import { formatString } from '../../../utils';

import { CartProduct } from '@buy-viasat/types/build/bv';

import styles from './checkoutCartOptionalServicesItem.module.scss';
import { useTranslation } from 'react-i18next';

type CheckoutCartOptionalServicesItemProps = {
  addon: CartProduct;
  isEditMode: boolean;
  onRemove: () => void;
};

const CheckoutCartOptionalServicesItem = ({ addon, isEditMode, onRemove }: CheckoutCartOptionalServicesItemProps) => {
  const locale = useSelector(selectLocale);
  const { t } = useTranslation();
  const currencySymbol = useSelector(selectCurrencySymbol);

  const basePrice = addon.price || 0;
  const promoPrice = addon.promo?.price || 0;
  const totalPrice = basePrice + promoPrice;

  const formattedPrice = priceDecimalFormat(basePrice, locale, currencySymbol, 2);
  const formattedTotalPrice = priceDecimalFormat(totalPrice, locale, currencySymbol, 2);

  return (
    <Surface mt="8px" variant="secondary" className={styles['item']} data-testid="cartOptionalServicesItem">
      <Txt variant="bodySmallRegular">{addon.name}</Txt>
      <Surface variant="secondary" data-testid="cartOptionalServicesPrice" className={styles['price']}>
        <div className={styles['priceText']}>
          {totalPrice > 0 ? (
            <Txt variant="bodySmallRegular">{t('common.pricePerMonth', { value: formattedTotalPrice })}</Txt>
          ) : (
            <Txt variant="bodySmallBold" color="success" className={styles['free']}>
              {t('common.free')}
            </Txt>
          )}

          {addon.promo &&
            (addon.promo.duration ? (
              <Txt variant="tinyItalic" color="subtle">
                {formatString(
                  t('common.pricePerMonthAfterPromo', {
                    value: formattedPrice,
                    duration: addon.promo.duration.toString(),
                  }),
                  addon.promo.duration,
                )}
              </Txt>
            ) : (
              <Txt variant="bodySmallRegular" color="subtle" className={styles['crossout']}>
                {formattedPrice}
              </Txt>
            ))}
        </div>

        {isEditMode && !addon.isBundle && (
          <Icon size={16} color="blue_600" ml="12px" mt="4px" onClick={onRemove} icon={RemoveCircleOutline} />
        )}
      </Surface>
    </Surface>
  );
};

export default CheckoutCartOptionalServicesItem;
